/**
 * Genetics Predictor Module
 * AI-powered predictions for litter outcomes
 * - Color/pattern predictions with Punnett squares
 * - Litter size estimation
 * - Health risk analysis
 * - Inbreeding coefficient calculator
 */

import { useState, useEffect } from 'react';
import { 
  Dna, Palette, Baby, Heart, AlertTriangle, CheckCircle, Info,
  ChevronRight, Calculator, Shield, TrendingUp, HelpCircle,
  Cat, Sparkles, BarChart3, FlaskConical, RefreshCw
} from 'lucide-react';
import { Card, Button, Badge, Select, Input, Alert, Tabs } from '../shared/UIComponents';
import { cn, formatDate } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { 
  CAT_COLORS, BREED_GENETICS, getBreedList, getColorList,
  predictKittenColors, predictLitterSize, analyzeHealthRisks, calculateCOI
} from '../../utils/catGenetics';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

// ===================
// MAIN COMPONENT
// ===================

export const GeneticsPredictor = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('color');
  const [myCats, setMyCats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selection state
  const [selectedQueen, setSelectedQueen] = useState(null);
  const [selectedSire, setSelectedSire] = useState(null);
  
  // Manual input state (if no cats in system)
  const [manualMode, setManualMode] = useState(false);
  const [manualQueen, setManualQueen] = useState({ color: '', breed: '', age: '' });
  const [manualSire, setManualSire] = useState({ color: '', breed: '' });

  useEffect(() => {
    if (user?.catteryId) loadMyCats();
  }, [user?.catteryId]);

  const loadMyCats = async () => {
    try {
      setLoading(true);
      const catsQuery = query(
        collection(db, 'cats'),
        where('catteryId', '==', user.catteryId)
      );
      const snapshot = await getDocs(catsQuery);
      const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMyCats(cats);
      
      // If no cats, enable manual mode
      if (cats.length === 0) setManualMode(true);
    } catch (error) {
      console.error('Error loading cats:', error);
    } finally {
      setLoading(false);
    }
  };

  const queens = myCats.filter(c => c.gender === 'female');
  const sires = myCats.filter(c => c.gender === 'male');

  const tabs = [
    { id: 'color', label: 'Color Predictor', icon: Palette },
    { id: 'litter', label: 'Litter Size', icon: Baby },
    { id: 'health', label: 'Health Risks', icon: Heart },
    { id: 'inbreeding', label: 'COI Calculator', icon: Calculator },
  ];

  // Get current prediction data
  const getQueenData = () => manualMode ? manualQueen : selectedQueen;
  const getSireData = () => manualMode ? manualSire : selectedSire;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900 flex items-center gap-2">
            <Dna className="w-7 h-7 text-feline-500" />
            Genetics Predictor
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
              <Sparkles className="w-3 h-3" /> AI-Powered
            </Badge>
          </h1>
          <p className="text-gray-500">Predict litter outcomes based on feline genetics</p>
        </div>
        <Button variant="outline" onClick={() => setManualMode(!manualMode)}>
          {manualMode ? <Cat className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}
          {manualMode ? 'Use My Cats' : 'Manual Mode'}
        </Button>
      </div>

      {/* Parent Selection */}
      <Card className="bg-gradient-to-r from-feline-50 to-purple-50 border-feline-200">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Cat className="w-5 h-5 text-feline-500" />
          Select Parents for Prediction
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Queen Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              👸 Queen (Mother)
            </label>
            
            {manualMode ? (
              <div className="space-y-2">
                <Select
                  value={manualQueen.breed}
                  onChange={(e) => setManualQueen({ ...manualQueen, breed: e.target.value })}
                  options={getBreedList().map(b => ({ value: b, label: b }))}
                  placeholder="Select breed..."
                />
                <Select
                  value={manualQueen.color}
                  onChange={(e) => setManualQueen({ ...manualQueen, color: e.target.value })}
                  options={getColorList().map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Select color..."
                />
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="20"
                  value={manualQueen.age}
                  onChange={(e) => setManualQueen({ ...manualQueen, age: e.target.value })}
                  placeholder="Age in years..."
                />
              </div>
            ) : (
              <Select
                value={selectedQueen?.id || ''}
                onChange={(e) => setSelectedQueen(queens.find(q => q.id === e.target.value))}
                options={queens.map(q => ({ value: q.id, label: `${q.name} (${q.breed}, ${q.color})` }))}
                placeholder={queens.length ? "Select queen..." : "No queens registered"}
              />
            )}
            
            {(selectedQueen || manualQueen.color) && (
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <div 
                  className="w-8 h-8 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: CAT_COLORS[manualMode ? manualQueen.color : selectedQueen?.color]?.hex || '#888' }}
                />
                <span className="text-sm text-gray-600">
                  {CAT_COLORS[manualMode ? manualQueen.color : selectedQueen?.color]?.name || 'Unknown color'}
                </span>
              </div>
            )}
          </div>

          {/* Sire Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              🦁 Sire (Father)
            </label>
            
            {manualMode ? (
              <div className="space-y-2">
                <Select
                  value={manualSire.breed}
                  onChange={(e) => setManualSire({ ...manualSire, breed: e.target.value })}
                  options={getBreedList().map(b => ({ value: b, label: b }))}
                  placeholder="Select breed..."
                />
                <Select
                  value={manualSire.color}
                  onChange={(e) => setManualSire({ ...manualSire, color: e.target.value })}
                  options={getColorList().map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Select color..."
                />
              </div>
            ) : (
              <Select
                value={selectedSire?.id || ''}
                onChange={(e) => setSelectedSire(sires.find(s => s.id === e.target.value))}
                options={sires.map(s => ({ value: s.id, label: `${s.name} (${s.breed}, ${s.color})` }))}
                placeholder={sires.length ? "Select sire..." : "No sires registered"}
              />
            )}
            
            {(selectedSire || manualSire.color) && (
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <div 
                  className="w-8 h-8 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: CAT_COLORS[manualMode ? manualSire.color : selectedSire?.color]?.hex || '#888' }}
                />
                <span className="text-sm text-gray-600">
                  {CAT_COLORS[manualMode ? manualSire.color : selectedSire?.color]?.name || 'Unknown color'}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all',
                activeTab === tab.id
                  ? 'bg-feline-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'color' && (
        <ColorPredictorTab queen={getQueenData()} sire={getSireData()} manualMode={manualMode} />
      )}
      {activeTab === 'litter' && (
        <LitterSizeTab queen={getQueenData()} sire={getSireData()} manualMode={manualMode} />
      )}
      {activeTab === 'health' && (
        <HealthRisksTab queen={getQueenData()} sire={getSireData()} manualMode={manualMode} />
      )}
      {activeTab === 'inbreeding' && (
        <InbreedingTab queen={getQueenData()} sire={getSireData()} />
      )}

      {/* Educational Note */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About These Predictions</p>
            <p>
              These predictions are based on known feline genetics principles and breed statistics. 
              Actual results may vary due to hidden recessive genes, incomplete dominance, and natural variation. 
              Always consult with a veterinary geneticist for breeding decisions.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ===================
// COLOR PREDICTOR TAB
// ===================

const ColorPredictorTab = ({ queen, sire, manualMode }) => {
  const [predictions, setPredictions] = useState([]);
  
  useEffect(() => {
    if ((queen?.color || queen?.id) && (sire?.color || sire?.id)) {
      const queenColor = queen.color;
      const sireColor = sire.color;
      
      if (queenColor && sireColor) {
        const results = predictKittenColors(
          { color: queenColor },
          { color: sireColor }
        );
        setPredictions(results);
      }
    } else {
      setPredictions([]);
    }
  }, [queen, sire]);

  if (!queen?.color && !queen?.id) {
    return <EmptyState message="Select a queen to see color predictions" />;
  }
  if (!sire?.color && !sire?.id) {
    return <EmptyState message="Select a sire to see color predictions" />;
  }

  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Palette className="w-5 h-5 text-purple-500" />
        Predicted Kitten Colors
      </h3>

      {predictions.length === 0 ? (
        <p className="text-gray-500">Unable to calculate predictions</p>
      ) : (
        <div className="space-y-4">
          {/* Color Probability Bars */}
          <div className="space-y-3">
            {predictions.map((pred, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: pred.hex }}
                    />
                    <span className="font-medium text-gray-900">{pred.name}</span>
                    {pred.femaleOnly && (
                      <Badge className="bg-pink-100 text-pink-700 text-xs">♀ Only</Badge>
                    )}
                  </div>
                  <span className="font-semibold text-feline-600">{pred.probability}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${pred.probability}%`,
                      backgroundColor: pred.hex === '#ffffff' ? '#e5e7eb' : pred.hex
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Visual Kitten Preview */}
          <div className="mt-6 pt-6 border-t">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Possible Kitten Colors</h4>
            <div className="flex flex-wrap gap-3">
              {predictions.filter(p => p.probability >= 10).map((pred, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div 
                    className="w-12 h-12 rounded-full border-4 border-white shadow-lg"
                    style={{ backgroundColor: pred.hex }}
                  />
                  <span className="text-xs text-gray-500">{pred.probability}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Genetics Note */}
          <Alert type="info" className="mt-4">
            <p className="text-sm">
              <strong>Note:</strong> Hidden recessive genes from ancestors can produce unexpected colors. 
              These predictions assume standard dominant/recessive inheritance patterns.
            </p>
          </Alert>
        </div>
      )}
    </Card>
  );
};

// ===================
// LITTER SIZE TAB
// ===================

const LitterSizeTab = ({ queen, sire, manualMode }) => {
  const [prediction, setPrediction] = useState(null);
  const [previousLitters, setPreviousLitters] = useState('');
  const [isFirstLitter, setIsFirstLitter] = useState(false);

  const breed = queen?.breed || sire?.breed || 'Other';
  const queenAge = parseFloat(queen?.age) || 3;

  useEffect(() => {
    if (breed) {
      const prevLitterArray = previousLitters
        .split(',')
        .map(n => parseInt(n.trim()))
        .filter(n => !isNaN(n) && n > 0);
      
      const result = predictLitterSize(breed, queenAge, prevLitterArray, isFirstLitter);
      setPrediction(result);
    }
  }, [breed, queenAge, previousLitters, isFirstLitter]);

  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Baby className="w-5 h-5 text-pink-500" />
        Litter Size Prediction
      </h3>

      {/* Input Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Previous Litter Sizes (comma separated)
          </label>
          <Input
            value={previousLitters}
            onChange={(e) => setPreviousLitters(e.target.value)}
            placeholder="e.g., 4, 5, 3"
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank if no history</p>
        </div>
        
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isFirstLitter}
              onChange={(e) => setIsFirstLitter(e.target.checked)}
              className="w-5 h-5 text-feline-500 rounded"
            />
            <span className="text-sm text-gray-700">This is the queen's first litter</span>
          </label>
        </div>
      </div>

      {prediction && (
        <div className="space-y-6">
          {/* Main Prediction */}
          <div className="text-center p-6 bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl">
            <p className="text-sm text-gray-600 mb-2">Expected Litter Size</p>
            <div className="flex items-center justify-center gap-4">
              <span className="text-6xl font-bold text-feline-600">{prediction.predicted}</span>
              <div className="text-left">
                <p className="text-lg text-gray-500">kittens</p>
                <p className="text-sm text-gray-400">Range: {prediction.range[0]} - {prediction.range[1]}</p>
              </div>
            </div>
            <Badge className={cn(
              'mt-3',
              prediction.confidence === 'high' ? 'bg-paw-100 text-paw-700' :
              prediction.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-700'
            )}>
              {prediction.confidence === 'high' ? '📊 High' : 
               prediction.confidence === 'medium' ? '📈 Medium' : '📉 Low'} Confidence
            </Badge>
          </div>

          {/* Factors */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-xs text-gray-500 mb-1">Breed Average</p>
              <p className="text-2xl font-bold text-gray-900">{prediction.factors.breed}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-xs text-gray-500 mb-1">Age Factor</p>
              <p className={cn(
                'text-lg font-semibold',
                prediction.factors.age === 'favorable' ? 'text-paw-600' :
                prediction.factors.age === 'reduced' ? 'text-amber-600' : 'text-gray-600'
              )}>
                {prediction.factors.age === 'favorable' ? '↑ Optimal' :
                 prediction.factors.age === 'reduced' ? '↓ Reduced' : '→ Normal'}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-xs text-gray-500 mb-1">History</p>
              <p className="text-sm font-medium text-gray-700">{prediction.factors.history}</p>
            </div>
          </div>

          {/* Breed Info */}
          {BREED_GENETICS[breed] && (
            <Alert type="info">
              <p className="text-sm">
                <strong>{breed}</strong>: Typical litter range is {BREED_GENETICS[breed].litterRange[0]}-{BREED_GENETICS[breed].litterRange[1]} kittens 
                with an average of {BREED_GENETICS[breed].avgLitterSize}.
              </p>
            </Alert>
          )}
        </div>
      )}
    </Card>
  );
};

// ===================
// HEALTH RISKS TAB
// ===================

const HealthRisksTab = ({ queen, sire, manualMode }) => {
  const breed = queen?.breed || sire?.breed || 'Other';
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (breed) {
      const result = analyzeHealthRisks(breed, { age: parseFloat(queen?.age) || 3 }, {});
      setAnalysis(result);
    }
  }, [breed, queen]);

  if (!analysis) {
    return <EmptyState message="Select a breed to see health risk analysis" />;
  }

  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Heart className="w-5 h-5 text-red-500" />
        Health Risk Analysis
        <Badge className={cn(
          analysis.overallRisk === 'high' ? 'bg-red-100 text-red-700' :
          analysis.overallRisk === 'medium' ? 'bg-amber-100 text-amber-700' :
          'bg-paw-100 text-paw-700'
        )}>
          {analysis.overallRisk.toUpperCase()} RISK
        </Badge>
      </h3>

      {/* Warnings */}
      {analysis.warnings.length > 0 && (
        <div className="mb-6 space-y-2">
          {analysis.warnings.map((warning, i) => (
            <Alert key={i} type="error">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">{warning}</span>
              </div>
            </Alert>
          ))}
        </div>
      )}

      {/* Risk Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Condition</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Risk Level</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Test Available</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {analysis.risks.map((risk, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="py-3 px-4">
                  <span className="font-medium text-gray-900">{risk.condition}</span>
                </td>
                <td className="py-3 px-4">
                  <Badge className={cn(
                    risk.severity === 'very high' || risk.severity === 'high' ? 'bg-red-100 text-red-700' :
                    risk.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                    risk.severity === 'low' ? 'bg-blue-100 text-blue-700' :
                    'bg-paw-100 text-paw-700'
                  )}>
                    {risk.severity}
                  </Badge>
                </td>
                <td className="py-3 px-4">
                  {risk.testAvailable ? (
                    <span className="flex items-center gap-1 text-paw-600">
                      <CheckCircle className="w-4 h-4" /> Yes
                    </span>
                  ) : (
                    <span className="text-gray-400">No</span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-600">
                  {risk.status || 'Unknown'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recommendations */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Recommendations</h4>
        <div className="grid gap-2">
          {analysis.recommendations.map((rec, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-blue-800">{rec}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Breed Notes */}
      {analysis.breedNotes && (
        <Alert type="info" className="mt-6">
          <p className="text-sm">
            <strong>Breed Notes:</strong> {analysis.breedNotes}
          </p>
        </Alert>
      )}
    </Card>
  );
};

// ===================
// INBREEDING TAB
// ===================

const InbreedingTab = ({ queen, sire }) => {
  const [coiResult, setCoiResult] = useState(null);

  // For demo purposes, show a simulated result
  useEffect(() => {
    if (queen && sire) {
      // Simulated COI - in real app, would use pedigree data
      setCoiResult({
        coi: 3.12,
        status: 'low',
        recommendation: 'Low inbreeding - good genetic diversity',
        commonAncestors: 1,
        note: 'Based on 4-generation pedigree analysis'
      });
    } else {
      setCoiResult(null);
    }
  }, [queen, sire]);

  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calculator className="w-5 h-5 text-indigo-500" />
        Coefficient of Inbreeding (COI)
      </h3>

      <Alert type="info" className="mb-6">
        <p className="text-sm">
          The Coefficient of Inbreeding measures how closely related two cats are. 
          Lower is better - ideally under 5% for healthy offspring.
        </p>
      </Alert>

      {!queen || !sire ? (
        <EmptyState message="Select both parents to calculate inbreeding coefficient" />
      ) : (
        <div className="space-y-6">
          {/* COI Display */}
          <div className="text-center p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl">
            <p className="text-sm text-gray-600 mb-2">Inbreeding Coefficient</p>
            <div className="text-6xl font-bold text-indigo-600 mb-2">
              {coiResult?.coi || '?'}%
            </div>
            <Badge className={cn(
              'text-sm',
              coiResult?.status === 'excellent' || coiResult?.status === 'low' ? 'bg-paw-100 text-paw-700' :
              coiResult?.status === 'moderate' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            )}>
              {coiResult?.status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
          </div>

          {/* Recommendation */}
          <div className={cn(
            'p-4 rounded-xl',
            coiResult?.status === 'excellent' || coiResult?.status === 'low' ? 'bg-paw-50 border border-paw-200' :
            coiResult?.status === 'moderate' ? 'bg-amber-50 border border-amber-200' :
            'bg-red-50 border border-red-200'
          )}>
            <p className="font-medium">{coiResult?.recommendation}</p>
          </div>

          {/* COI Scale */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">COI Reference Scale</h4>
            <div className="space-y-2">
              {[
                { range: '0-3%', label: 'Excellent', color: 'bg-paw-500' },
                { range: '3-6%', label: 'Low', color: 'bg-paw-400' },
                { range: '6-12%', label: 'Moderate', color: 'bg-amber-400' },
                { range: '12-25%', label: 'High', color: 'bg-orange-500' },
                { range: '>25%', label: 'Dangerous', color: 'bg-red-500' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={cn('w-4 h-4 rounded', item.color)} />
                  <span className="text-sm text-gray-600 w-16">{item.range}</span>
                  <span className="text-sm font-medium text-gray-900">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Note about pedigree data */}
          <Alert type="warning">
            <p className="text-sm">
              <strong>Note:</strong> Accurate COI calculation requires complete pedigree data for both parents 
              going back at least 4 generations. Register your cats' pedigrees to enable full analysis.
            </p>
          </Alert>
        </div>
      )}
    </Card>
  );
};

// ===================
// EMPTY STATE
// ===================

const EmptyState = ({ message }) => (
  <Card className="text-center py-12">
    <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
    <p className="text-gray-500">{message}</p>
  </Card>
);

export default GeneticsPredictor;
